import { React, useState, useRef, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';

import { useLanguage } from '../../Context/LanguageContext';
import { useTheme } from '../../Context/ThemeContext';
import { lightTheme, darkTheme } from '../../Styles/theme';

import { ReactComponent as LeftArrowSvg } from '../../image/LeftArrow.svg';
import { ReactComponent as PlusSvg } from '../../image/Plus.svg';

export default function NewClassSelectBox(props) {
	const { selectClickFn, value, setValue, options, color } = props;
	const history = useHistory();
	const { t } = useLanguage();
	const { isDark } = useTheme();
	const theme = isDark ? darkTheme : lightTheme;

	const [isClicked, setIsClicked] = useState(false);
	const [dropdownHeight, setDropdownHeight] = useState(0);

	const TopLevelRef = useRef();

	useEffect(() => {
		const optionsHeight = options.length * 32;
		setDropdownHeight(Math.min(optionsHeight + 58, 330));
	}, [options]);

	useEffect(() => {
		document.addEventListener("mousedown", onClickOutSide);
		return () => {
			document.removeEventListener("mousedown", onClickOutSide);
		}
	}, []);

	function onClickOutSide(e) {
		if (TopLevelRef.current !== e.target && !TopLevelRef.current.contains(e.target)) {
			setIsClicked(false);
		}
	}

	function onClick() {
		selectClickFn();
		setIsClicked(!isClicked);
	}

	function optionalClick(e, index) {
		e.stopPropagation();
		setValue(options[index]);
		setIsClicked(false);
	}

	function onClickBreakTime(e) {
		e.stopPropagation();
		setValue({
			id: -2,
			name: '브레이크 타임',
			maxBookedPeople: 0,
			teacherIds: '',
			teachers: [],
			time: 0
		});
		setIsClicked(false);
	}

	function onClickNewClass(e) {
		e.stopPropagation();
		window.open('/setting', '_blank');
		setIsClicked(false);
	}

	return (
		<TopLevelWrapper ref={TopLevelRef}>
			<SelectView isClicked={isClicked} onClick={onClick}>
				<ArrowIcon isClicked={isClicked} />
				<SelectedClassOptionBoxWrapper>
					<SelectedClassOptionColorTag color={color} />
					<SelectedClassOptionBox>
						<SelectedOptionText>{value.name}</SelectedOptionText>
					</SelectedClassOptionBox>
				</SelectedClassOptionBoxWrapper>
			</SelectView>
			<DropdownBox isClicked={isClicked} height={isClicked ? dropdownHeight : 0}>
				{options.map((data, index) =>
					<OptionWrapper key={index} isLast={index === options.length - 1} onClick={(e) => optionalClick(e, index)}>
						<Option>{data.name}</Option>
					</OptionWrapper>
				)}
				<BreakTimeWrapper onClick={onClickBreakTime}>
					<BreakTimeText>{t('classManage.breakTimeLabel')}</BreakTimeText>
				</BreakTimeWrapper>
				<GoSettingWrapper onClick={onClickNewClass}>
					<GoSettingText>{t('classManage.newClassSetup')}</GoSettingText>
					<GoSettingBox>
						<GoSettingImage />
					</GoSettingBox>
				</GoSettingWrapper>
			</DropdownBox>
		</TopLevelWrapper>
	);
}

const TopLevelWrapper = styled.div`
	position: relative;
	display: flex;
	flex-direction: column;

	justify-content: flex-start;
	align-items: flex-start;

	width: 100%;
	height: 32px;
`;

const SelectView = styled.div`
	display: flex;
	flex-direction: row;

	justify-content: space-between;
	align-items: center;

	padding: 0px 5px;

	box-sizing: border-box;
	width: 100%;
	height: 32px;

	border: 1px solid ${props => props.theme.line};
	border-top-left-radius: 5px;
	border-top-right-radius: 5px;
	border-bottom-left-radius: ${props => props.isClicked ? `0px` : `5px`};
	border-bottom-right-radius: ${props => props.isClicked ? `0px` : `5px`};

	background-color: ${props => props.theme.subBackground};

	@media (hover: hover) {
		&:hover {
			cursor: pointer;
		}
	}

	transition: all 0.3s ease-in-out;
`;

const ArrowIcon = styled(LeftArrowSvg)`
	position: absolute;
	top: 10px;
	right: 10px;
	transform: ${props => props.isClicked ? 'rotate(90deg)' : 'rotate(-90deg)'};

	width: 8px;
	height: 13px;

	color: ${props => props.theme.line};

	pointer-events: none;
	transition: transform 0.3s ease-in-out;
`;

const SelectedClassOptionBoxWrapper = styled.div`
	display: flex;
	flex-direction: row;

	align-items: center;

	height: 22px;

	border-radius: 2px;
	background-color: ${props => props.theme.background};
	overflow: hidden;
`;

const SelectedClassOptionColorTag = styled.div`
	width: 6px;
	height: 22px;

	background-color: ${props => props.color};
	border-radius: 2px 0 0 2px;
	flex-shrink: 0;
`;

const SelectedClassOptionBox = styled.div`
	display: flex;
	flex-direction: row;

	align-items: center;

	padding: 0 5px 0 10px;
	height: 22px;

	min-width: 0;
`;

const SelectedOptionText = styled.p`
	font-family: NotoSansKR-Regular;
	font-size: 14px;

	color: ${props => props.theme.text};

	margin: 0;

	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
`;

const DropdownBox = styled.div`
	position: absolute;
	display: flex;
	flex-direction: column;

	justify-content: flex-start;
	align-items: center;

	margin-top: 32px;

	width: calc(100% - 2px);
	height: ${props => props.height}px;

	border-left: 1px solid ${props => props.theme.line};
	border-right: 1px solid ${props => props.theme.line};
	border-bottom: ${props => props.isClicked ? `1px solid ${props.theme.line}` : `0px`};
	border-bottom-left-radius: ${props => props.isClicked ? `5px` : `0px`};
	border-bottom-right-radius: ${props => props.isClicked ? `5px` : `0px`};

	background-color: ${props => props.theme.subBackground};
	z-index: 10;

	overflow-x: hidden;
	overflow-y: scroll;

	&::-webkit-scrollbar {
		width: 0px;
	}

	&::-webkit-scrollbar-thumb {
		box-shadow: inset 0 0 10px 20px ${props => props.theme.line};
		border: 3px solid transparent;
		height: 25px;
		border-radius: 200px;
	}

	&::-webkit-scrollbar-track {
		box-shadow: inset 0 0 10px 10px transparent;
		border: 4px solid transparent;
		background-color: transparent;
	}

	&::-webkit-scrollbar-track-piece {
		background-color: transparent;
	}

	transition: all 0.3s ease-in-out;
`;

const OptionWrapper = styled.div`
	display: flex;
	flex-direction: row;

	align-items: center;

	box-sizing: border-box;
	width: 100%;
	height: 32px;
	min-height: 32px;

	border: 1px solid transparent;
	border-bottom: ${props => props.isLast ? `0px` : `1px solid ${props.theme.line}`};
	background-color: transparent;

	@media (hover: hover) {
		&:hover {
			cursor: pointer;
			border-color: ${props => props.theme.primary};
		}
	}

	transition: all 0.3s ease-in-out;
`;

const BreakTimeWrapper = styled.div`
	display: flex;
	flex-direction: row;

	justify-content: center;
	align-items: center;

	width: 100%;
	height: 29px;
	min-height: 29px;

	background-color: ${props => props.theme.line};

	gap: 4px;

	@media (hover: hover) {
		&:hover {
			cursor: pointer;
		}
	}
`;

const BreakTimeText = styled.p`
	font-family: NotoSansKR-Medium;
	font-size: 14px;

	color: ${props => props.theme.text};

	margin: 0 0 0 0;
	padding: 0 0 0 0;

	@media (hover: hover) {
		&:hover {
			cursor: pointer;
		}
	}
`;

const GoSettingWrapper = styled.div`
	display: flex;
	flex-direction: row;

	justify-content: center;
	align-items: center;

	width: 100%;
	height: 29px;
	min-height: 29px;

	background-color: ${props => props.theme.primary};

	gap: 4px;

	@media (hover: hover) {
		&:hover {
			cursor: pointer;
		}
	}
`;

const GoSettingText = styled.p`
	font-family: NotoSansKR-Regular;
	font-size: 14px;

	color: ${props => props.theme.subBackground};

	margin: 0 0 0 0;
	padding: 0 0 0 0;

	@media (hover: hover) {
		&:hover {
			cursor: pointer;
		}
	}
`;

const GoSettingBox = styled.div`
	display: flex;
	flex-direction: column;

	justify-content: center;
	align-items: center;

	width: 16px;
	height: 16px;

	border-radius: 4px;
	background-color: ${props => props.theme.primary};

	@media (hover: hover) {
		&:hover {
			cursor: pointer;
		}
	}
`;

const GoSettingImage = styled(PlusSvg)`
	flex-shrink: 0;
	width: 10px;
	height: 10px;

	& path { fill: ${props => props.theme.subBackground}; }

	@media (hover: hover) {
		&:hover {
			cursor: pointer;
		}
	}
`;

const Option = styled.p`
	font-family: NotoSansKR-Regular;
	font-size: 14px;

	color: ${props => props.theme.text};

	margin: 6px 0 6px 8px;
`;
